import { useMemo, useState } from "react";
import { useFetch } from "hooks/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "utils/api/index";
import Loading from "components/core/blocks/Loading";
import HarvestingConfiguration from "./HarvestingConfiguration";
import Card from "components/core/basic/Card";
import ProgramList from "views/programs/ProgramList";
import { useModuleEnabled } from "redux/selectors/modules";
import { ProgramContextProvider } from "components/custom/programs/ProgramContext";
import LookbackWindowConfiguration from "./LookbackWindowConfiguration";
import { successToast, errorToast } from "../../utils/toasts";
import { PageContainer } from "../../components/core/basic/PageContainer";
import { VStack } from "../../../styled-system/jsx";
import { css } from "../../../styled-system/css";
import Tabs from "components/core/basic/Tabs";
import HarvestingRules from "./HavestingRules";
import { DatesProvider } from "dates/DatesContext";
import useQueryParams from "hooks/useQueryParams";
import AmazonPortfolios from "components/custom/AmazonPortfolios";

const TABS = [
  {
    key: "rules",
    label: "Rules",
    children: [
      {
        key: "bidding",
        label: "Bidding",
      },
      {
        key: "harvesting",
        label: "Harvesting",
      },
      {
        key: "custom_harvesting",
        label: "Custom Harvesting",
      },
      {
        key: "campaigns",
        label: "Campaigns",
      },
      {
        key: "lineitem_bidding",
        label: "Line Items",
      },
    ],
  },
  {
    key: "algorithms",
    label: "Bidding Algorithms",
    children: [
      {
        key: "lookbackWindow",
        label: "Lookback Window",
      },
    ],
  },
  {
    key: "portfolios",
    label: "Portfolios",
  },
];

const OrgAdvertisingConfiguration = () => {
  const [harvestingSettings, setHarvestingSettings] = useState(null);
  const [errors, setErrors] = useState({});

  const queryClient = useQueryClient();
  const hasDSPModule = useModuleEnabled("DSP");
  const hasPortfoliosModule = useModuleEnabled("PORTFOLIOS");
  const [tab, setTab] = useQueryParams("tab", "bidding");

  const orgHarvestingSettingsQuery = useFetch(
    ["orgHarvestingSettings"],
    "/org_bid_budget_settings",
    {},
    {
      default: [],
      select: (d) => d.data,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    },
  );

  const harvestingSettingsResolved = harvestingSettings ?? {
    harvestingClicks: orgHarvestingSettingsQuery.data?.harvesting_clicks,
    harvestingConversions: orgHarvestingSettingsQuery.data?.harvesting_conversions,
    harvestingDays: orgHarvestingSettingsQuery.data?.harvesting_days,
    harvestingViewImpressions: orgHarvestingSettingsQuery.data?.harvesting_view_impressions,
  };

  const saveHarvestingSettings = useMutation({
    mutationFn: async (data) => {
      await api.put(`/org_bid_budget_settings`, data);
    },

    onSuccess: () => {
      successToast({ message: "Successfully Updated Harvesting Settings" });

      queryClient.invalidateQueries();
    },

    onError: (error) => {
      errorToast({ message: "Failed to Update Harvesting Settings", error });
    },
  });

  const updateHarvestingSettings = () => {
    const { harvestingClicks, harvestingConversions, harvestingDays, harvestingViewImpressions } =
      harvestingSettingsResolved;

    const settingsData = {
      harvesting_clicks: isNaN(Number.parseFloat(harvestingClicks)) ? null : Number.parseFloat(harvestingClicks),
      harvesting_conversions:
        isNaN(Number.parseFloat(harvestingConversions)) ? null : Number.parseFloat(harvestingConversions),
      harvesting_days: isNaN(Number.parseFloat(harvestingDays)) ? null : Number.parseFloat(harvestingDays),
      harvesting_view_impressions:
        isNaN(Number.parseFloat(harvestingViewImpressions)) ? null : Number.parseFloat(harvestingViewImpressions),
    };

    saveHarvestingSettings.mutate(settingsData);
  };
  const handleChange = (e) => {
    if (
      Object.values({
        ...harvestingSettingsResolved,
        [e.target.name]: e.target.value,
      }).some((setting) => !setting)
    ) {
      setErrors({ harvestingRules: "All fields are required." });
    } else {
      setErrors({});
    }

    setHarvestingSettings({
      ...harvestingSettingsResolved,
      [e.target.name]: e.target.value,
    });
  };

  const tabs = useMemo(
    () =>
      TABS.filter((tab) => {
        if (tab.key === "portfolios") {
          return hasPortfoliosModule;
        }
        return true;
      }).map((tab) => {
        if (tab.key === "rules") {
          return {
            ...tab,
            children: tab.children.filter((child) => child.key !== "lineitem_bidding" || hasDSPModule),
          };
        }
        return tab;
      }),
    [hasDSPModule, hasPortfoliosModule],
  );

  if (orgHarvestingSettingsQuery.isLoading) {
    return <Loading fullPage={true} />;
  }

  return (
    <PageContainer>
      <VStack alignItems={"stretch"} width="100%" paddingX="xxlarge" paddingY="large" alignSelf={"center"} gap="medium">
        <h3
          className={css({
            margin: 0,
            fontWeight: "bold",
            textAlign: "center",
          })}
        >
          Advertising - Configuration
        </h3>
        <Card>
          <Tabs tabs={tabs} selectedTab={tab} onTabChange={setTab}>
            {tab === "bidding" && (
              <ProgramContextProvider engine={"bidding"}>
                <h3 className="fw-bold pb-4 pt-1">Bidding Rules</h3>
                <ProgramList engine={"bidding"} biddingLayout={true} embedded />
              </ProgramContextProvider>
            )}
            {tab === "campaigns" && (
              <ProgramContextProvider engine={"budget"}>
                <h3 className="fw-bold pb-4 pt-1">Campaign Rules</h3>
                <ProgramList engine={"budget"} biddingLayout={true} embedded />
              </ProgramContextProvider>
            )}
            {tab === "lineItems" && hasDSPModule && (
              <ProgramContextProvider engine={"lineitem_bidding"}>
                <h3 className="fw-bold pb-4 pt-1">Line Item Bidding Rules</h3>
                <ProgramList
                  engine={"lineitem_bidding"}
                  biddingLayout={true}
                  title="Line Item Bidding Rules"
                  embedded
                />
              </ProgramContextProvider>
            )}
            {tab === "harvesting" && (
              <HarvestingConfiguration
                handleChange={handleChange}
                state={harvestingSettingsResolved}
                onChange={(partialSettings) => {
                  if (
                    Object.values({
                      ...harvestingSettingsResolved,
                      ...partialSettings,
                    }).some((setting) => !setting && isNaN(parseInt(setting)))
                  ) {
                    setErrors({
                      harvestingRules: "All fields are required.",
                    });
                  } else {
                    setErrors({});
                  }
                  setHarvestingSettings({
                    ...harvestingSettingsResolved,
                    ...partialSettings,
                  });
                }}
                saveHarvestingSettings={updateHarvestingSettings}
                disableSubmitButton={saveHarvestingSettings.isPending || Object.keys(errors)?.length}
                orgLayout={true}
              />
            )}
            {tab === "lookbackWindow" && <LookbackWindowConfiguration />}
            {tab === "lineitem_bidding" && (
              <ProgramContextProvider engine={"lineitem_bidding"}>
                <ProgramList
                  engine={"lineitem_bidding"}
                  biddingLayout={true}
                  title="Line Item Bidding Rules"
                  embedded
                />
              </ProgramContextProvider>
            )}
            {tab === "custom_harvesting" && (
              <DatesProvider>
                <HarvestingRules />
              </DatesProvider>
            )}
            {tab === "portfolios" && <AmazonPortfolios />}
          </Tabs>
        </Card>
      </VStack>
    </PageContainer>
  );
};

export default OrgAdvertisingConfiguration;
